<template>
  <div class="card">
    <div class="card-body">
      <div class="login-card-heading">
        <h2 class="heading2">{{ $t('login.verifySecurityCode') }}</h2>
      </div>
      <div v-if="availableMethodsCount > 1">
        <div v-if="emailMask" class="flex">
          <UIRadioButton
            :label="$t('login.sendCodeToEmail')"
            id="otp-for-email"
            name="otp"
            :selectedValue="otpChannel"
            @update:modelValue="changeOtpChannel('email')"
            modelValue="email"
            :disabled="lastOtpSent > 0 && isTimerRunning && otpChannel !== 'email'"
          />
          <p class="ml-2">{{ emailMask }}</p>
        </div>
        <div v-if="phoneMask" class="flex mt-3">
          <UIRadioButton
            :label="$t('login.sendCodeToPhone')"
            id="otp-for-phone"
            name="otp"
            :selectedValue="otpChannel"
            @update:modelValue="changeOtpChannel('phone')"
            modelValue="phone"
            :disabled="lastOtpSent > 0 && isTimerRunning && otpChannel !== 'phone'"
          />
          <p class="ml-2">{{ phoneMask }}</p>
        </div>
        <div v-if="totpEnabled" class="flex mt-3">
          <UIRadioButton
            :label="$t('campaign.regulatoryBundle.auth.otp.useAuthenticator')"
            id="totp-for-authenticator"
            name="otp"
            :selectedValue="otpChannel"
            @input="changeOtpChannel('totp')"
            value="totp"
            :disabled="lastOtpSent > 0 && isTimerRunning && otpChannel !== 'totp'"
          />
        </div>
      </div>
      <div v-if="!firstTime" class="mt-4 form-group">
        <div v-if="!backUpCodeEnabled" class="flex flex-row justify-center px-2 text-center">
          <OtpInput
            ref="otpInput1"
            input-classes="otp-input"
            separator="-"
            :num-inputs="6"
            :should-auto-focus="true"
            input-type="number"
            @on-change="handleOnChange"
            @on-complete="handleOnComplete"
          />
        </div>
        <div v-if="backUpCodeEnabled" class="w-full">
          <UITextInputGroup
            id="backup-code"
            v-model="backupCode"
            type="text"
            name="backup-code"
            :placeholder="$t('campaign.regulatoryBundle.auth.otp.enterBackupCode')"
            :disabled="loading"
          />
        </div>
      </div>
      <div class="mt-4">
        <LoginUIAlert v-if="response" type="danger">
          {{ response }}
        </LoginUIAlert>
        <LoginUIAlert v-else-if="(loading || !response) && userFeedback" type="info" :loading="loading && !firstTime">
          {{ userFeedback }}
        </LoginUIAlert>
        <div style="display: inline-block; position: relative; width: 100%">
          <UIButton
            v-if="firstTime && otpChannel !== 'totp'"
            use="tertiary"
            @click.prevent="submit()"
            :loading="loading"
            :disabled="(!firstTime && !isValidOtp) || loading"
            class="justify-center w-full"
          >
            {{ loading ? $t('campaign.regulatoryBundle.auth.otp.sending') : $t('common.send') }}
            {{ $t('login.securityCode') }}
          </UIButton>
          <UIButton
            v-if="backUpCodeEnabled && otpChannel === 'totp'"
            use="tertiary"
            @click.prevent="submitWithBackupCode()"
            :loading="loading"
            :disabled="loading"
            class="justify-center w-full"
          >
            {{ $t('campaign.regulatoryBundle.auth.otp.verifyButton') }}
          </UIButton>
        </div>
      </div>

      <div
        v-if="!firstTime && !firebaseError && !maxAttemptsReached && otpChannel !== 'totp'"
        class="w-full font-semibold text-center cursor-pointer"
        :class="lastOtpSent === allowOTPAfter ? 'text-curious-blue-500 hover:text-curious-blue-800' : 'text-gray-300'"
        @click="requestForOtp"
        v-text="`${$t('campaign.regulatoryBundle.campaign.emailOTPModal.resendCode')} ${allowOTPAfter-lastOtpSent === 0 ? '' : `(${allowOTPAfter-lastOtpSent}s)`}`"
      >
      </div>
      <div
        v-if="otpChannel === 'totp' && backUpCodeEnabled"
        class="w-full font-semibold text-center cursor-pointer mt-2"
        :class="
          backUpCodeEnabled && loading ? 'text-gray-300 mt-2' : 'text-curious-blue-500 hover:text-curious-blue-800'
        "
        @click="loading ? null : switchBackUpCode()"
        v-text="`${$t('campaign.regulatoryBundle.auth.otp.useAuthenticator')}`"
      ></div>
      <div
        v-if="otpChannel === 'totp' && !backUpCodeEnabled"
        class="w-full font-semibold text-center cursor-pointer"
        :class="backUpCodeEnabled ? 'text-gray-300 mt-2' : 'text-curious-blue-500 hover:text-curious-blue-800'"
        @click="backUpCodeEnabled = true"
        v-text="`${$t('campaign.regulatoryBundle.auth.otp.useBackupCode')}`"
      ></div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { OauthService } from '@/services'
import OtpInput from './OtpInput.vue'
import { handleSuccessfulLogin, handleLoginError } from '../utils/LoginHelper'
import LoginUIAlert from '../components/LoginUIAlert.vue'
import { getHostAndDomain } from '../../util/helper'
import { i18n } from '@/localization'

export default defineComponent({
  name: 'LoginOTPScreen',
  components: {
    OtpInput,
    LoginUIAlert,
  },
  props: {
    email: {
      type: String,
      required: true,
    },
    password: {
      type: String,
      required: true,
    },
    emailMask: {
      type: String,
    },
    phoneMask: {
      type: String,
    },
    totpEnabled: {
      type: Boolean,
    },
    selectedCompanyId: {
      type: String,
    },
    fromShopify: {
      type: Boolean,
    },
  },
  data() {
    return {
      response: '',
      loading: false,
      domainCompanyId: null,
      firstTime: true,
      otpChannel: this.emailMask ? 'email' : this.totpEnabled ? 'totp' : 'phone',
      otpCode: '',
      backupCode: '',
      token: '',
      lastOtpSent: 0,
      allowOTPAfter: 30,
      otpAttempt: 0,
      userFeedback: '',
      firebaseError: false,
      isAutoChannelSwitch: false,
      maxAttemptsReached: false,
      backUpCodeEnabled: false,
    }
  },
  computed: {
    isValidOtp() {
      return this.otpCode.length === 6
    },
    isTimerRunning() {
      return this.allowOTPAfter - this.lastOtpSent > 0
    },
    availableMethodsCount() {
      let count = 0
      if (this.emailMask) count++
      if (this.phoneMask) count++
      if (this.totpEnabled) count++
      return count
    }
  },
  watch: {
    otpChannel() {
      if (!this.isAutoChannelSwitch) {
        this.userFeedback = ''
        this.response = ''
      }
      this.isAutoChannelSwitch = false
    },
  },
  methods: {
    async submit() {
      if (this.firstTime && this.otpChannel !== 'totp') {
        return this.requestForOtp()
      }
      if (this.otpChannel === 'totp') {
        this.firstTime = false
      }
      return this.verifyOtp()
    },
    async requestForOtp() {
      if (this.loading || (this.firstTime === false && this.lastOtpSent !== this.allowOTPAfter)) {
        return
      }

      try {
        this.response = ''
        this.resetOtpInput()
        this.userFeedback = !this.firstTime ? i18n.global.t('login.sendingOTP') : ''
        this.lastOtpSent = 0
        this.otpAttempt++
        this.loading = true

        const params = {
          ...getHostAndDomain(),
          email: this.email.trim(),
          password: this.password.trim(),
          deviceId: await this.$store.dispatch('getDeviceId'),
          deviceType: 'web',
          deviceName: navigator.userAgent,
          otpChannel: this.otpChannel,
        } as { [key: string]: any }

        if (this.domainCompanyId && this.domainCompanyId !== 'YuTUZlUtrwBtvmgByZDW') {
          params.companyId = this.domainCompanyId || this.selectedCompanyId
        } else {
          params.companyId = this.selectedCompanyId
        }

        const response = await OauthService.loginWithEmail(params)
        const contactInfo = this.otpChannel === 'email' ? this.emailMask : this.phoneMask
        this.userFeedback = i18n.global.t('login.OTPResponse', {
          otpChannel: contactInfo,
          receivedType: this.otpChannel === 'email' ? 'inbox' : 'phone',
        })
        this.token = response.token
        this.firstTime = false
        this.lastOtpSent = 1
        this.changeCounter()
      } catch (error) {
        const { errorMessage, errorType, maxAttemptsReached } = handleLoginError(error, this.email)
        this.maxAttemptsReached = maxAttemptsReached
        if (!this.maxAttemptsReached) {
          this.switchOtpChannel(errorMessage)
        }

        if (errorType === 'too-many-requests') {
          this.response = errorMessage
        }

        if (errorType === 'firebase-connection-error') {
          this.firebaseError = true
        }
      } finally {
        this.loading = false
      }
    },
    switchOtpChannel(errorMessage: string) {
      if (this.emailMask && this.phoneMask) {
        const newOtpChannel = this.otpChannel === 'email' ? 'phone' : 'email'
        this.isAutoChannelSwitch = true
        this.response = i18n.global.t('login.autoChannelSwitch', {
          otpChannel: this.otpChannel,
          newOtpChannel: newOtpChannel,
        })
        this.changeOtpChannel(newOtpChannel)
      } else {
        this.response = errorMessage
      }
    },
    switchBackUpCode() {
      this.backUpCodeEnabled = !this.backUpCodeEnabled
      this.response = ''
    },
    async verifyOtp() {
      try {
        this.loading = true
        this.response = ''
        this.userFeedback = this.otpCode ? i18n.global.t('login.verifyingOTP') : i18n.global.t('login.sendingOTP')

        const params = {
          ...getHostAndDomain(),
          email: this.email.trim(),
          password: this.password.trim(),
          deviceId: await this.$store.dispatch('getDeviceId'),
          deviceType: 'web',
          deviceName: navigator.userAgent,
          otpChannel: this.otpChannel,
          otp: this.otpCode,
          token: this.token,
          backupCode: this.backUpCodeEnabled,
        } as { [key: string]: any }

        if (this.domainCompanyId && this.domainCompanyId !== 'YuTUZlUtrwBtvmgByZDW') {
          params.companyId = this.domainCompanyId || this.selectedCompanyId
        } else {
          params.companyId = this.selectedCompanyId
        }

        const response = await OauthService.loginWithEmail(params)

        this.userFeedback = i18n.global.t('login.accountReady')

        await handleSuccessfulLogin(response, this.$store, this.$router)

        this.userFeedback = i18n.global.t('login.redirectingAccount')
      } catch (error) {
        const { errorMessage, errorType } = handleLoginError(error, this.email)
        this.response = errorMessage

        if (errorType === 'firebase-connection-error') {
          this.firebaseError = true
        }
        this.resetOtpInput()
      } finally {
        this.loading = false
      }
    },
    handleOnChange(value) {
      this.otpCode = value
    },
    handleOnComplete(value) {
      this.otpCode = value
      this.submit()
    },
    resetOtpInput() {
      this.otpCode = ''
      this.userFeedback = ''
      if (this.$refs.otpInput1) {
        ;(this.$refs.otpInput1 as any).clearInput()
      }
    },
    changeCounter() {
      if (this.lastOtpSent && this.lastOtpSent < this.allowOTPAfter) {
        this.lastOtpSent++
        setTimeout(this.changeCounter, 1000)
      }
    },
    changeOtpChannel(option: 'email' | 'phone' | 'totp') {
      this.firstTime = true
      this.backUpCodeEnabled = false
      this.lastOtpSent = 0
      this.otpChannel = option
      if (option === 'totp') {
        this.firstTime = false
      }
    },
    async submitWithBackupCode() {
      this.otpCode = this.backupCode
      if (this.otpCode.length === 10) {
        await this.verifyOtp()
      } else {
        this.userFeedback = i18n.global.t('login.invalidBackupCode')
      }
    }
  },
  async created() {
    // Auto-send OTP if only one method is available (skip TOTP as it doesn't require sending)
    if (this.availableMethodsCount === 1) {
      if (this.otpChannel === 'totp') {
        // For TOTP-only, show the input field immediately (no OTP to send)
        this.firstTime = false
      } else {
        // For email/phone-only, auto-send the OTP
        await this.requestForOtp()
      }
    }
  }
})
</script>

<style scoped>
.error {
  color: #a94442;
}
.hl_login--body .spinner {
  display: inline-block;
  margin: 40px 0px 0px 0px;
  width: 100%;
}
.has-error .form-control,
.has-error .form-control:focus {
  -webkit-box-shadow: none;
  box-shadow: none;
}
.global-error {
  margin-bottom: 10px;
}
.hl_login--body .v-spinner .v-moon1 {
  margin: auto;
}
</style>
